import { SECTION_FILL_IN_BLANK_EXAMPLAR } from '../sections/FillInBlankSection';
import { SECTION_MULTIPLE_CHOICE_EXAMPLAR } from '../sections/MultipleChoiceSection';
import { callDeepSeekApi } from '../../../common/functions/AIUtils';
import { parseLlmJson } from '../../../common/functions/parseLlmJson';

/** Extract and parse JSON from a DeepSeek text response. */
export function parseAiJsonResponse(rawText) {
    return parseLlmJson(rawText);
}

export function transformFillInBlankResponse(responseJson) {
    const parsed = parseAiJsonResponse(responseJson?.output?.text);
    const sentences = parsed?.sentences || [];
    const questions = [];

    sentences.forEach((sentenceRow) => {
        const vocabulary = sentenceRow?.vocabulary;
        const sentence = sentenceRow?.sentence;
        if (!vocabulary || !sentence) {
            return;
        }
        const parts = sentence.split(vocabulary);
        const tokens = parts
            .map((part) => [{ type: 'text', value: part }, { type: 'answer', value: vocabulary }])
            .flat();
        tokens.pop();
        questions.push({ blocks: tokens });
    });

    return {
        title: parsed?.title,
        questions,
    };
}

export function transformMultipleChoiceResponse(responseJson) {
    const parsed = parseAiJsonResponse(responseJson?.output?.text);
    return normalizeMultipleChoiceSection(parsed);
}

export function normalizeMultipleChoiceSection(data) {
    const rawQuestions = data?.questions || [];
    const questions = rawQuestions
        .map((question) => {
            const text = (question?.text || question?.question || '').trim();
            const rawChoices = question?.choices || question?.options || [];
            const choices = rawChoices
                .map((choice) => {
                    if (typeof choice === 'string') {
                        return { type: 'text', value: choice.trim() };
                    }
                    const value = (choice?.value || choice?.text || '').trim();
                    if (!value) {
                        return null;
                    }
                    return { type: choice?.type || 'text', value };
                })
                .filter(Boolean);

            if (!text || choices.length < 2) {
                return null;
            }

            return {
                type: question?.type || 'text',
                text,
                choices,
            };
        })
        .filter(Boolean);

    return {
        title: data?.title,
        questions,
    };
}

function normalizeMaterialContent(content) {
    return (content || '')
        .replace(/\\n/g, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/** Parse AI material response: JSON with title/subtitle/content, or plain text fallback. */
export function parseMaterialResponse(rawText) {
    const trimmed = (rawText || '').trim();
    if (!trimmed) {
        return { title: null, subtitle: null, content: '' };
    }

    try {
        const parsed = parseAiJsonResponse(trimmed);
        const content = normalizeMaterialContent(
            parsed?.content || parsed?.body || parsed?.text || parsed?.passage || ''
        );
        if (content) {
            return {
                title: parsed?.title?.trim() || null,
                subtitle: parsed?.subtitle?.trim() || null,
                content,
            };
        }
    } catch (error) {
        // fall through to plain text
    }

    return {
        title: null,
        subtitle: null,
        content: normalizeMaterialContent(stripMarkdownFences(trimmed)),
    };
}

function stripMarkdownFences(text) {
    return text
        .replace(/^```(?:json|markdown)?\s*\n?/gim, '')
        .replace(/\n?```\s*$/gim, '')
        .trim();
}

export function derivePaperTitleAndSubtitle({
    userTitle,
    userSubtitle,
    materialTitle,
    materialSubtitle,
    sectionTitle,
    sectionLabels,
    language,
    grade,
    difficultyLabel,
}) {
    const title =
        userTitle?.trim() ||
        materialTitle ||
        sectionTitle ||
        language.defaultTitle;

    const labels = (sectionLabels && sectionLabels.length)
        ? sectionLabels
        : [language.fillInBlankTitle, language.multipleChoiceTitle];

    const subtitle =
        userSubtitle?.trim() ||
        materialSubtitle ||
        [grade, labels.filter(Boolean).join(' · '), difficultyLabel]
            .filter(Boolean)
            .join(' · ');

    return { title, subtitle };
}

function materialJsonInstruction(targetLanguageKey) {
    const exemplar = JSON.stringify(
        {
            title: 'Article title',
            subtitle: 'Brief topic summary',
            content: 'First paragraph line one.\nFirst paragraph line two.\n\nSecond paragraph here.',
        },
        null,
        2
    );

    switch (targetLanguageKey) {
        case 'en':
            return `Respond with ONLY a valid JSON object (no markdown fences, no commentary) in this shape:\n${exemplar}\nPut the full reading passage in "content". Use a single line break (\\n) within a paragraph and a blank line (\\n\\n) between paragraphs. Use "title" for the article headline and "subtitle" for a one-line topic summary.`;
        case 'zh-Hans':
            return `请只回答一个有效的 JSON 对象（不要 markdown 代码块，不要额外说明），格式如下：\n${exemplar}\n将完整阅读文章放在 "content"。段内换行用单个换行（\\n），段落之间用空行（\\n\\n）分隔。"title" 为文章标题，"subtitle" 为一行主题摘要。`;
        case 'zh-Hant':
        default:
            return `請只回答一個有效的 JSON 對象（不要 markdown 代碼塊，不要額外說明），格式如下：\n${exemplar}\n將完整閱讀文章放在 "content"。段內換行用單個換行（\\n），段落之間用空行（\\n\\n）分隔。"title" 為文章標題，"subtitle" 為一行主題摘要。`;
    }
}

export function buildMaterialGenerationPrompt(aiPrompt, grade, difficultyKey, targetLanguageKey, wordCount = 1200) {
    const difficultyLabel = { easy: 'Easy', medium: 'Medium', difficult: 'Difficult' }[difficultyKey] || 'Medium';
    const jsonInstruction = materialJsonInstruction(targetLanguageKey);
    const length = Math.max(50, parseInt(wordCount, 10) || 300);

    switch (targetLanguageKey) {
        case 'en': {
            const gradeHint = grade ? `suitable for ${grade}, ` : '';
            const topicHint = aiPrompt.trim() ? `Topic or requirements: ${aiPrompt.trim()}.` : '';
            return [
                `Write a reading passage in English, ${gradeHint}at "${difficultyLabel}" difficulty, about ${length} words.`,
                'Use clear language and rich vocabulary suitable for fill-in-the-blank and reading comprehension exercises.',
                topicHint,
                jsonInstruction,
            ].filter(Boolean).join(' ');
        }
        case 'zh-Hans': {
            const gradeHint = grade ? `适合${grade}程度、` : '';
            const topicHint = aiPrompt.trim() ? `主题或要求：${aiPrompt.trim()}。` : '';
            return [
                `请用简体中文撰写一篇${gradeHint}难度为「${difficultyLabel}」的中文阅读文章，约${length}字，`,
                '内容清晰、用词丰富，适合作为词汇填空及阅读理解练习的素材。',
                topicHint,
                jsonInstruction,
            ].filter(Boolean).join('');
        }
        case 'zh-Hant':
        default: {
            const gradeHint = grade ? `適合${grade}程度、` : '';
            const topicHint = aiPrompt.trim() ? `主題或要求：${aiPrompt.trim()}。` : '';
            return [
                `請用繁體中文撰寫一篇${gradeHint}難度為「${difficultyLabel}」的中文閱讀文章，約${length}字，`,
                '內容清晰、用詞豐富，適合作為詞彙填空及閱讀理解練習的素材。',
                topicHint,
                jsonInstruction,
            ].filter(Boolean).join('');
        }
    }
}

function strictJsonSuffix(exemplar) {
    return [
        'IMPORTANT: Reply with ONLY valid JSON. No markdown code fences, no explanation before or after.',
        'Every string value MUST use ASCII double quotes ", even for Chinese text.',
        'CORRECT: "text": "「轉換表達方式」的範圍"',
        'WRONG: "text": 「轉換表達方式」的範圍',
        'Each question must have "type": "text", "text", and "choices" (array of {"type":"text","value":"..."}).',
        'Use this exact structure:',
        JSON.stringify(exemplar, null, 2),
    ].join('\n');
}

export function generateChineseQuizFillInBlankPrompt(numberOfQuestions, difficulty, languageCode) {
    switch (languageCode) {
        case 'en':
            return `Choose ${numberOfQuestions} key vocabularies from the above content. For each, form a ~30-word English sentence at difficulty ${difficulty}/5. Each vocabulary appears exactly once in its sentence.\n${strictJsonSuffix(SECTION_FILL_IN_BLANK_EXAMPLAR)}`;
        case 'zh-Hans':
            return `请从上文中挑选${numberOfQuestions}个词汇（非专有名词），并用每个词汇造句约二十字，难度10份之${difficulty * 2}。每个词汇在句子中只出现一次。用简体中文回答。\n${strictJsonSuffix(SECTION_FILL_IN_BLANK_EXAMPLAR)}`;
        case 'zh':
        default:
            return `請從上文中挑選${numberOfQuestions}個詞彙（非專有名詞），並用每個詞彙造句約二十字，難度10份之${difficulty * 2}。每個詞彙在句子中只出現一次。用繁體中文回答。\n${strictJsonSuffix(SECTION_FILL_IN_BLANK_EXAMPLAR)}`;
    }
}

export function generateChineseQuizMultipleChoicePrompt(numberOfQuestions, difficulty, languageCode) {
    switch (languageCode) {
        case 'en':
            return `Create ${numberOfQuestions} reading comprehension multiple-choice questions from the above content at difficulty ${difficulty}/5. Reply in English.\n${strictJsonSuffix(SECTION_MULTIPLE_CHOICE_EXAMPLAR)}`;
        case 'zh-Hans':
            return `请根据上文内容编写${numberOfQuestions}题阅读理解多项选择题，难度10份之${difficulty * 2}。用简体中文回答。\n${strictJsonSuffix(SECTION_MULTIPLE_CHOICE_EXAMPLAR)}`;
        case 'zh':
        default:
            return `請根據上文內容編寫${numberOfQuestions}題閱讀理解多項選擇題，難度10份之${difficulty * 2}。用繁體中文回答。\n${strictJsonSuffix(SECTION_MULTIPLE_CHOICE_EXAMPLAR)}`;
    }
}

/** Call DeepSeek and retry when the response fails JSON parsing. */
export async function callDeepSeekWithRetries(apiToken, context, prompt, parseFn, maxAttempts = 3, options = {}) {
    let currentPrompt = prompt;
    let lastError = null;
    const requestOptions = Object.assign({ jsonMode: true }, options);

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const response = await callDeepSeekApi(apiToken, context, currentPrompt, requestOptions);

        if (response?.success === false) {
            return { error: response.message || 'DeepSeek API error' };
        }

        try {
            const parsed = parseFn(response);
            return { response, parsed };
        } catch (error) {
            lastError = error;
            if (attempt < maxAttempts) {
                currentPrompt = `${prompt}\n\nYour previous reply was not valid JSON. Reply again with ONLY the JSON object. Every string value must use ASCII double quotes, e.g. "text": "「詞彙」的句子". Do not use 「」 as JSON delimiters. No markdown fences.`;
            }
        }
    }

    return { error: lastError?.message || 'AI response could not be parsed' };
}
